import { createSelector } from 'reselect';
import { QUESTIONNAIRE_COMPLETE_URL, QUESTIONNAIRE_PAGE_URL, SI_MENU_PAGESTATES } from 'src/constants/constants';
import urlParamsMapper from 'src/helpers/urlParamsMapper.js';

const questionnaireInitState = state => state.questionnaire;
export const getQuestionnaireState = questionnaireInitState;

export const deriveMenuAndIndexLegacy = (pageId, isCompletePage = false) =>
  createSelector([getQuestionnaireState], data => {
    const { intro, page } = data;

    /*
     * If we have data from showOrganizer we should use that instead of intro. If a user lands in the lobby
     * first though we have no choice but to use the intro data
     */

    let menuSource = null;
    let menuIndex = 0;

    if (page && page.menus) {
      menuSource = page;
    } else if (intro && intro.menus) {
      menuSource = intro;
    } else {
      return data.menu;
    }

    const languageId = 1;

    let newMenu = [];
    const { menus } = menuSource;
    let menuItemIndex = 0;
    let menuItem;
    const numOfMenus = menus.length;

    function updateNewMenu(subItem, subIndex, doNotBuildMiddlePage = true) {
      /*
       * Dynamic menus have their true pieces of content nested inside subMenus
       */
      if (subItem.subMenu.length) {
        return subItem.subMenu.forEach((item, index) => updateNewMenu(item, index, true));
      }

      if (subIndex === 0 && menuItemIndex > 0 && !doNotBuildMiddlePage) {
        const lastPageId = newMenu[newMenu.length - 1].pageId;
        const lastMenuName = menus[menuItemIndex - 1].menuName;
        const rootMenuUrl = urlParamsMapper(QUESTIONNAIRE_COMPLETE_URL, {
          ...menuSource,
          languageId,
          pageId: lastPageId,
          sectionName: lastMenuName,
        });
        newMenu.push({ ...menuItem, url: rootMenuUrl });
      }

      const { subMenu, ...menuData } = subItem;
      const subMenuUrl = urlParamsMapper(QUESTIONNAIRE_PAGE_URL, {
        ...menuSource,
        languageId,
        pageId: menuData.pageId,
        sectionName: menuItem.menuName,
      });

      if (pageId === menuData.pageId) {
        menuIndex = newMenu.length;
      }

      newMenu.push({ ...menuData, url: subMenuUrl });
    }

    for (menuItemIndex; menuItemIndex < numOfMenus; menuItemIndex += 1) {
      menuItem = menus[menuItemIndex];

      if (menuItem && menuItem.subMenu) {
        menuItem.subMenu.forEach((item, index) => updateNewMenu(item, index));
      }
    }

    if (isCompletePage) {
      menuIndex++;
    }

    return { menu: newMenu, index: menuIndex };
  });

export const deriveMenuAndIndex = (pageId, isCompletePage = false) =>
  createSelector([getQuestionnaireState], data => {
    const { intro, page } = data;

    /*
     * If we have data from showOrganizer we should use that instead of intro. If a user lands in the lobby
     * first though we have no choice but to use the intro data
     */

    let menuSource = null;
    let menuIndex = 0;

    if (page && page.menus) {
      menuSource = page;
    } else if (intro && intro.menus) {
      menuSource = intro;
    } else {
      return data.menu;
    }

    const languageId = 1;

    let newMenu = [];
    const { menus } = menuSource;
    let menuItemIndex = 0;
    let menuItem;
    const numOfMenus = menus.length;

    let menusComplete = 0;
    let totalPages = 0;
    const isMenuComplete = status => status === SI_MENU_PAGESTATES.COMPLETE || status === SI_MENU_PAGESTATES.CURRENT_COMPLETE;

    const sectionProgress = {};

    function updateNewMenu(subItem, subIndex, doNotBuildMiddlePage = true) {
      /*
       * Dynamic menus have their true pieces of content nested inside subMenus
       */
      if (subItem.subMenu.length) {
        return subItem.subMenu.forEach((item, index) => updateNewMenu(item, index, true));
      }

      /*
          If we are in a sectionLevel > the first section
          and we finally iterated past the first position we're going to use this as the time to build the
          middle pages that show the progress between sections.

          In the case of entity pages we are going to ignore this building of the middle page used to show progress between sections.
          This may not be an absolute fix but it is a first attempt.

        */

      if (subIndex === 0 && menuItemIndex > 0 && !doNotBuildMiddlePage) {
        const lastPageId = newMenu[newMenu.length - 1].pageId;
        const lastMenuName = menus[menuItemIndex - 1].menuName;
        const rootMenuUrl = urlParamsMapper(QUESTIONNAIRE_COMPLETE_URL, {
          ...menuSource,
          languageId,
          pageId: lastPageId,
          sectionName: lastMenuName,
        });
        newMenu.push({ ...menuItem, url: rootMenuUrl });
      }

      const { subMenu, ...menuData } = subItem;
      const subMenuUrl = urlParamsMapper(QUESTIONNAIRE_PAGE_URL, {
        ...menuSource,
        languageId,
        pageId: menuData.pageId,
        sectionName: menuItem.menuName,
      });

      if (typeof sectionProgress[menuItem.menuName] === 'undefined') {
        sectionProgress[menuItem.menuName] = { total: 0, complete: 0 };
      }

      if (pageId === menuData.pageId) {
        menuIndex = newMenu.length;
      }

      if (isMenuComplete(menuData.pageState)) {
        menusComplete++;
        sectionProgress[menuItem.menuName].complete++;
      }

      newMenu.push({ ...menuData, url: subMenuUrl });
      sectionProgress[menuItem.menuName].total++;
      totalPages++;
    }

    for (menuItemIndex; menuItemIndex < numOfMenus; menuItemIndex += 1) {
      menuItem = menus[menuItemIndex];

      if (menuItem && menuItem.subMenu) {
        menuItem.subMenu.forEach((item, index) => updateNewMenu(item, index));
      }
    }

    if (isCompletePage) {
      menuIndex++;
    }

    Object.keys(sectionProgress).forEach(key => {
      const section = sectionProgress[key];
      section.progress = Math.round((section.complete * 100) / section.total);
    });

    const progress = Math.floor((menusComplete * 100) / totalPages);
    return { menu: newMenu, index: menuIndex, progress, sectionProgress };
  });
